import React, { useCallback, useMemo } from 'react';
import Title from '../components/Title';
import AnchorageAfter from '../components/article/AnchorageAfter';
import LocationsAfter from '../components/article/LocationsAfter';
import RouteAfter from '../components/article/RouteAfter';
import ZoomTo from '../components/map/ZoomTo';
import useItem from '../hooks/useItem';
import { useParams, useNavigate } from 'react-router-dom';
import { SourceIndexes, titlePrefix } from '../config/SourceConfig';
import { buildRouteName } from '../utils/utils';
import { VectorSource } from '@avinet/react-openlayers';
import { featureStyle } from '../utils/featureStyle';
import { getMediaUrl } from '../utils/getMediaUrl';

import './ArticleDetailView.scss';

const Port = ({
  councilname,
  locationcode,
  locationnamenor,
  locationnameeng,
  locationgeometrylatitude,
  locationgeometrylongitude,
  type,
  locationid
}) => {
  return (
    <div className="article">
      <p className="articletype port">{titlePrefix(type)}</p>
      <h3>{locationnameeng}</h3>
      <div style={{ marginBottom: -10 }}>
        <div className="additional">
          <h5>Norwegian</h5>
          <p>{locationnamenor}</p>
        </div>
        <div className="additional">
          <h5>UN/LOCODE</h5>
          <p>{locationcode}</p>
        </div>
        <div className="additional">
          <h5>Municipality</h5>
          <p>{councilname}</p>
        </div>
      </div>
      <LocationsAfter
        locationid={locationid}
        type={type}
        lat={[locationgeometrylatitude]}
        lon={[locationgeometrylongitude]}
      />
    </div>
  );
};

const PortFacility = ({
  councilname,
  portfacilityno,
  locationnamenor,
  locationnameeng,
  type,
  locationid,
  locationgeometrylatitude,
  locationgeometrylongitude
}) => {
  return (
    <div className="article">
      <p className="articletype portfacility">{titlePrefix(type)}</p>
      <h3>{locationnameeng}</h3>
      <div style={{ marginBottom: -10 }}>
        <div className="additional">
          <h5>Norwegian</h5>
          <p>{locationnamenor}</p>
        </div>
        <div className="additional">
          <h5>Port Facility No</h5>
          <p>{portfacilityno}</p>
        </div>
        <div className="additional">
          <h5>Municipality</h5>
          <p>{councilname}</p>
        </div>
      </div>
      <LocationsAfter
        locationid={locationid}
        type={type}
        lat={[locationgeometrylatitude]}
        lon={[locationgeometrylongitude]}
      />
    </div>
  );
};

const Quay = ({
  councilname,
  locationcode,
  locationnamenor,
  locationnameeng,
  locationgeometrylatitude,
  locationgeometrylongitude,
  type,
  locationid
}) => {
  return (
    <div className="article">
      <p className="articletype quay">{titlePrefix(type)}</p>
      <h3>{locationnameeng}</h3>
      <div style={{ marginBottom: -10 }}>
        <div className="additional">
          <h5>Norwegian</h5>
          <p>{locationnamenor}</p>
        </div>
        <div className="additional">
          <h5>UN/LOCODE</h5>
          <p>{locationcode}</p>
        </div>
        <div className="additional">
          <h5>Municipality</h5>
          <p>{councilname}</p>
        </div>
      </div>
      <LocationsAfter
        locationid={locationid}
        type={type}
        lat={[locationgeometrylatitude]}
        lon={[locationgeometrylongitude]}
      />
    </div>
  );
};

const MTR = ({ section, region, factsheet, type }) => {
  return (
    <div className="article">
      <p className="articletype mtr">{titlePrefix(type)}</p>
      <h3>{section}</h3>
      <div className="additional">
        <h5>Section</h5>
        <p>{section}</p>
      </div>
      <div className="additional">
        <h5>Region</h5>
        <p>{region}</p>
      </div>
      <div>
        <a
          href={getMediaUrl(factsheet, 'mtr_' + section + '.pdf')}
          target="_blank"
          className="btn btn-info"
          rel="noopener noreferrer"
        >
          Download Factsheet
        </a>
      </div>
    </div>
  );
};

const CPR = ({
  name,
  pilotage_area,
  c23_day_maxlength,
  c23_night_maxlength,
  c23_day_maxdraught,
  c23_night_maxdraught,
  c1_day_maxlength,
  c1_night_maxlength,
  c1_day_maxdraught,
  c1_night_maxdraught,
  remarkenglish,
  sok_kl1,
  c1_dp,
  type
}) => {
  if (c1_dp === 0) {
    return (
      <div className="article">
        <p className="articletype cpr red">{titlePrefix(type)}</p>
        <h3>{name}</h3>
        <h4>Fairway with no PEC navigation</h4>
        <div className="additional">
          <h5>Pilotage area</h5>
          <p>{pilotage_area}</p>
        </div>
        <div className="additional">
          <h5>No PEC navigation</h5>
        </div>
        {remarkenglish && (
          <div className="additional">
            <h5>Remark</h5>
            <p>{remarkenglish}</p>
          </div>
        )}
      </div>
    );
  } else if (c1_dp === 1 && sok_kl1 !== 0) {
    return (
      <div className="article">
        <p className="articletype cpr blue">{titlePrefix(type)}</p>
        <h3>{name}</h3>
        <h4>PEC class 1 fairway</h4>
        <div className="additional">
          <h5>Pilotage area</h5>
          <p>{pilotage_area}</p>
        </div>

        {c23_day_maxlength > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, day max LOA</h5>
            <p>{c23_day_maxlength}</p>
          </div>
        )}
        {c23_night_maxlength > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, night max LOA</h5>
            <p>{c23_night_maxlength}</p>
          </div>
        )}
        {c23_day_maxdraught > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, day max draught</h5>
            <p>{c23_day_maxdraught}</p>
          </div>
        )}
        {c23_night_maxdraught > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, night max draught</h5>
            <p>{c23_night_maxdraught}</p>
          </div>
        )}
        {c1_day_maxlength > 0 && (
          <div className="additional">
            <h5>PEC class 1, day max LOA</h5>
            <p>{c1_day_maxlength}</p>
          </div>
        )}
        {c1_night_maxlength > 0 && (
          <div className="additional">
            <h5>PEC class 1, night max LOA</h5>
            <p>{c1_night_maxlength}</p>
          </div>
        )}
        {c1_day_maxdraught > 0 && (
          <div className="additional">
            <h5>PEC class 1, day max draught</h5>
            <p>{c1_day_maxdraught}</p>
          </div>
        )}
        {c1_night_maxdraught > 0 && (
          <div className="additional">
            <h5>PEC class 1, night max draught</h5>
            <p>{c1_night_maxdraught}</p>
          </div>
        )}
        {remarkenglish && (
          <div className="additional">
            <h5>Remark</h5>
            <p>{remarkenglish}</p>
          </div>
        )}
      </div>
    );
  } else if (c1_dp !== 0 && sok_kl1 === 0) {
    return (
      <div className="article">
        <p className="articletype cpr">{titlePrefix(type)}</p>
        <h3>{name}</h3>
        <div className="additional">
          <h5>Pilotage area</h5>
          <p>{pilotage_area}</p>
        </div>
        <h4>PEC class 2 fairway</h4>
        {c23_day_maxlength > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, day max LOA</h5>
            <p>{c23_day_maxlength}</p>
          </div>
        )}
        {c23_night_maxlength > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, night max LOA</h5>
            <p>{c23_night_maxlength}</p>
          </div>
        )}
        {c23_day_maxdraught > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, day max draught</h5>
            <p>{c23_day_maxdraught}</p>
          </div>
        )}
        {c23_night_maxdraught > 0 && (
          <div className="additional">
            <h5>PEC class 2 / 3, night max draught</h5>
            <p>{c23_night_maxdraught}</p>
          </div>
        )}
        {remarkenglish && (
          <div className="additional">
            <h5>Remark</h5>
            <p>{remarkenglish}</p>
          </div>
        )}
      </div>
    );
  }
};

const Route = ({
  calcLength,
  ssn_port_facilities,
  ssn_ports,
  ssn_quays,
  id,
  route_name,
  route_number,
  route_quality_status,
  validity_period_start,
  type,
  olFeature,
  route_line
}) => {
  const routeName = useMemo(
    () =>
      buildRouteName({
        routeQualityStatus: route_quality_status,
        routeName: route_name,
        date: validity_period_start
      }),
    [route_quality_status, route_name, validity_period_start]
  );

  return (
    <div className="article">
      <p className="articletype route">{titlePrefix(type)}</p>
      <h3>{route_name}</h3>
      <RouteAfter
        id={id}
        olFeature={olFeature}
        routeLine={route_line}
        routeName={routeName}
        routeNumber={route_number}
        autoLength={calcLength}
        portIds={ssn_ports}
        portfacilityIds={ssn_port_facilities}
        quayIds={ssn_quays}
      />
    </div>
  );
};

const Svalbard = ({ name, factsheet, type }) => {
  return (
    <div className="article">
      <p className="articletype svalbard">{titlePrefix(type)}</p>
      <h3>{name}</h3>
      <div>
        <a
          href={getMediaUrl(factsheet, 'svalbard_' + name + '.pdf')}
          target="_blank"
          className="btn btn-info"
          rel="noopener noreferrer"
        >
          Download Factsheet
        </a>
      </div>
    </div>
  );
};

// const LFR = ({ title, text_en, pilotage_area, type }) => {
//   useEffect(() => {
//     function renderHtmlFromString(htmlString, targetElementId) {
//       const tempContainer = document.createElement('div');

//       tempContainer.innerHTML = htmlString;

//       const targetElement = document.getElementById(targetElementId);

//       if (!targetElement) {
//         console.error('Target element not found!');
//         return;
//       }

//       while (tempContainer.firstChild) {
//         targetElement.appendChild(tempContainer.firstChild);
//       }
//     }

//     renderHtmlFromString(text_en, 'textToHtml');
//   }, [text_en]);

//   return (
//     <div className="article">
//       <p className="articletype lfr">{titlePrefix(type)}</p>
//       <h3>{title}</h3>
//       <div className="additional">
//         <h5>Pilotage area</h5>
//         <p>{pilotage_area}</p>
//       </div>
//       <div className="additional" id="textToHtml"></div>
//     </div>
//   );
// };

const Anchorage = (rec) => {
  return (
    <div className="article">
      <p className="articletype anchorage">{titlePrefix(rec.type)}</p>
      <AnchorageAfter
        {...rec}
      />
    </div>
  );
};

const ArticleDetailView = () => {
  const { sourceId, articleId } = useParams();
  const { item } = useItem({
    type: sourceId,
    id: sourceId !== 'route' ? parseInt(articleId) : articleId
  });

  const navigate = useNavigate();

  const renderArticle = useCallback(() => {
    switch (item.type) {
      case SourceIndexes.PORT:
        return <Port {...item} />;
      case SourceIndexes.PORT_FACILITY:
        return <PortFacility {...item} />;
      case SourceIndexes.QUAY:
        return <Quay {...item} />;
      case SourceIndexes.MTR:
        return <MTR {...item} />;
      case SourceIndexes.CPR:
        return <CPR {...item} />;
      case SourceIndexes.ROUTE:
        return <Route {...item} />;
      case SourceIndexes.SVALBARD:
        return <Svalbard {...item} />;
      case SourceIndexes.ANCHORAGE:
        return <Anchorage {...item} />;
      default:
        break;
    }
  }, [item]);

  return (
    <div className="article-detail-view--container">
      {item && (
        <>
          <ZoomTo features={[item.olFeature]} />
          <VectorSource
            features={[item.olFeature]}
            layerName="detail_view_layer"
            selectable={false}
            style={featureStyle}
            zIndex={4}
          />
        </>
      )}
      <Title title="Route Info - Kystverket" />
      <button className="closeBtn" onClick={() => navigate('/')} title={'Close'}>
        <span className="glyphicon glyphicon-remove" />
      </button>
      {item && renderArticle()}
    </div>
  );
};

export default ArticleDetailView;
